/**
 * SourceGenerator Types for MEV-Med Frontend
 * Type definitions for source generator management (fontes geradoras)
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de fontes geradoras
 */

import type { DependencyCheck } from './dependencyCheck';

/**
 * Enum for SourceGenerator status
 */
export enum SourceGeneratorStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  DECOMMISSIONED = 'DECOMMISSIONED'
}

/**
 * Enum for Risk Type - aligned with backend values
 */
export enum SourceGeneratorRiskType {
  PHYSICAL = 'FISICO',
  CHEMICAL = 'QUIMICO',
  BIOLOGICAL = 'BIOLOGICO',
  ERGONOMIC = 'ERGONOMICO',
  ACCIDENT = 'ACIDENTE',
  OTHER = 'OUTRO'
}

/**
 * Maintenance status (calculated from dates)
 */
export interface MaintenanceStatus {
  hasScheduledMaintenance: boolean;
  isOverdue: boolean;
  isDueSoon: boolean;
  daysUntilNext: number | null;
}

/**
 * SourceGenerator entity interface
 */
export interface SourceGenerator {
  id: number;
  name: string;
  location: string;
  riskType: SourceGeneratorRiskType;
  description: string;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  yearOfManufacture?: number | null;
  maintenanceFrequency?: string | null;
  lastMaintenanceDate?: string | null;
  nextMaintenanceDate?: string | null;
  observations?: string | null;
  status: SourceGeneratorStatus;
  maintenanceStatus?: MaintenanceStatus;
  // Alert counts (optional - returned when includeCounts=true)
  _counts?: {
    companies?: number;
    departments?: number;
    occupations?: number;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Filters for listing source generators
 */
export interface SourceGeneratorFilters {
  search?: string;
  riskType?: SourceGeneratorRiskType;
  status?: SourceGeneratorStatus;
  location?: string;
  maintenanceOverdue?: boolean;
  maintenanceDueSoon?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Form data for creating/updating source generators
 */
export interface SourceGeneratorFormData {
  name: string;
  location: string;
  riskType: SourceGeneratorRiskType;
  description: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  yearOfManufacture?: number | null;
  maintenanceFrequency?: string;
  lastMaintenanceDate?: string | null;
  nextMaintenanceDate?: string | null;
  observations?: string;
  status: SourceGeneratorStatus;
}

/**
 * API payload for creating/updating source generators
 */
export interface SourceGeneratorApiPayload {
  name: string;
  location: string;
  riskType: string;
  description: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  yearOfManufacture?: number | null;
  maintenanceFrequency?: string;
  lastMaintenanceDate?: string | null;
  nextMaintenanceDate?: string | null;
  observations?: string;
  status: SourceGeneratorStatus;
}

/**
 * Response for list endpoint with pagination
 */
export interface SourceGeneratorListResponse {
  sourceGenerators: SourceGenerator[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Dependency check response
 */
export type SourceGeneratorDependencies = DependencyCheck;

/**
 * Maintenance alert
 */
export interface MaintenanceAlert {
  id: number;
  name: string;
  location: string;
  nextMaintenanceDate: string;
  daysUntilMaintenance: number;
  status: 'overdue' | 'due_soon' | 'ok';
}

/**
 * Props for UI components
 */
export interface SourceGeneratorTableProps {
  sourceGenerators: SourceGenerator[];
  onEdit: (sourceGenerator: SourceGenerator) => void;
  onDelete: (id: number) => void;
  onView: (sourceGenerator: SourceGenerator) => void;
  loading?: boolean;
}

export interface SourceGeneratorFormProps {
  initialData?: SourceGenerator;
  onSubmit: (data: SourceGeneratorFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface SourceGeneratorFiltersProps {
  filters: SourceGeneratorFilters;
  onChange: (filters: SourceGeneratorFilters) => void;
  onSearch: () => void;
}

/**
 * Risk type label mapping for UI display
 */
export const RISK_TYPE_LABELS: Record<SourceGeneratorRiskType, string> = {
  [SourceGeneratorRiskType.PHYSICAL]: 'Físico',
  [SourceGeneratorRiskType.CHEMICAL]: 'Químico',
  [SourceGeneratorRiskType.BIOLOGICAL]: 'Biológico',
  [SourceGeneratorRiskType.ERGONOMIC]: 'Ergonômico',
  [SourceGeneratorRiskType.ACCIDENT]: 'Acidente',
  [SourceGeneratorRiskType.OTHER]: 'Outro',
};

/**
 * Status label mapping for UI display
 */
export const STATUS_LABELS: Record<SourceGeneratorStatus, string> = {
  [SourceGeneratorStatus.ACTIVE]: 'Ativo',
  [SourceGeneratorStatus.INACTIVE]: 'Inativo',
  [SourceGeneratorStatus.MAINTENANCE]: 'Em Manutenção',
  [SourceGeneratorStatus.DECOMMISSIONED]: 'Descomissionado',
};

/**
 * Status color mapping for Chips
 */
export const STATUS_COLORS: Record<SourceGeneratorStatus, 'success' | 'default' | 'warning' | 'error'> = {
  [SourceGeneratorStatus.ACTIVE]: 'success',
  [SourceGeneratorStatus.INACTIVE]: 'default',
  [SourceGeneratorStatus.MAINTENANCE]: 'warning',
  [SourceGeneratorStatus.DECOMMISSIONED]: 'error',
};

/**
 * Risk type color mapping for Chips
 */
export const RISK_TYPE_COLORS: Record<SourceGeneratorRiskType, 'error' | 'warning' | 'success' | 'info' | 'primary' | 'secondary'> = {
  [SourceGeneratorRiskType.PHYSICAL]: 'primary',
  [SourceGeneratorRiskType.CHEMICAL]: 'error',
  [SourceGeneratorRiskType.BIOLOGICAL]: 'warning',
  [SourceGeneratorRiskType.ERGONOMIC]: 'info',
  [SourceGeneratorRiskType.ACCIDENT]: 'secondary',
  [SourceGeneratorRiskType.OTHER]: 'primary',
};
